import { useContext, useEffect } from 'react'
import useRemoteDesktopControlPermissionActions, {
    ScreenControlRequestWarning,
} from '../../services/useRemoteDesktopControlPermissionActions'
import { RoomContext, RoomContextType } from '../../RoomContext'
import { Trans, useTranslation } from 'react-i18next'
import { AnalyticsEvents } from '../../services/AnalyticsService'
import useAnalytics from '../../services/useAnalytics'
import { Alert } from '@/UIComponents2/Alert'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { DialogBody, DialogTitle } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import useRoomViews from '@/services/useRoomViews'

function RemoteDesktopControlAskPermissionPopup({
    name,
    onClose,
}: {
    name: string
    onClose: (askForPermission: boolean) => void
}) {
    const { t } = useTranslation()

    const { presentingPinnedPeerSessionId } = useRoomViews()

    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { screenControlRequestWarning: warning } = useRemoteDesktopControlPermissionActions()

    const title = warning.canControl ? t('Take control of this desktop?') : t("Can't control desktop")

    // stop showing dialog if `presentingPinnedPeerSessionId` gets invalidated.
    useEffect(() => {
        if (!presentingPinnedPeerSessionId) {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isRemoteDesktopControlQueryPromptOpen: null,
                },
            })
        }
    }, [roomDispatch, presentingPinnedPeerSessionId])

    return (
        <CrossPlatformDialog forceBrowserModal onDefaultClose={() => onClose(false)}>
            <Contents title={title} name={name} warning={warning} onClose={onClose} />
        </CrossPlatformDialog>
    )
}

function Contents({
    title,
    name,
    warning,
    onClose,
}: {
    title: string
    name: string
    warning: ScreenControlRequestWarning
    onClose: (askForPermission: boolean) => void
}) {
    const { t } = useTranslation()

    const { logUserActivityWithContext } = useAnalytics()

    if (warning.canControl) {
        return (
            <DialogBody>
                <DialogTitle>{title}</DialogTitle>
                <div className="pb-2">
                    <Trans
                        i18nKey="You need to ask <1>{{name}}</1> for permission to control this desktop."
                        values={{ name }}
                    >
                        You need to ask <span style={{ color: '#f9cc04' }}>{'{{name}}'}</span> for permission to control
                        this desktop.
                    </Trans>
                </div>
                <div className="pb-2">{t('Do you want to continue?')}</div>
                {warning.showVersionWarning && (
                    <Alert variant="solid" level="warning" className="my-2">
                        {t(
                            "Looks like {{name}}'s app version is outdated. Please update the app if you " +
                                'encounter issues while controlling the screen.',
                            { name }
                        )}
                    </Alert>
                )}
                <DialogActionButtonsSection
                    rightButtonText={t('Request Control')}
                    onRightButtonClicked={() => {
                        onClose(true)
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_REQUEST_SCREEN_CONTROL_BUTTON, {
                            screenOwner: name,
                        })
                    }}
                />
            </DialogBody>
        )
    } else {
        if (warning.showUnsupportedClientWarning) {
            return (
                <DialogBody>
                    <div className="pb-3 pt-3">
                        {t(
                            'Looks like {{name}} is using a client that does not support screen control. ' +
                                'Screen control is supported on the Muse desktop app.',
                            { name }
                        )}
                    </div>
                </DialogBody>
            )
        } else {
            return (
                <DialogBody>
                    <div className="pb-3 pt-3">
                        {t(
                            'This screen is not controllable because it is an application window. ' +
                                'You can only control the screen if the entire screen is shared.'
                        )}
                    </div>
                </DialogBody>
            )
        }
    }
}

export default RemoteDesktopControlAskPermissionPopup
